import React from "react";
import { Helmet } from "react-helmet-async";
import TriathlonLocation from "./Components/MapLocate";
import { loadEpreuve } from "./requests";
import { coord } from "./Components/MapLocate";
import { QueryTriathlon } from "./Components/QueryTriathlon";
import moment from "moment";
// import images and icons
import ImageTriathlonDetail from "./medias/calendrier_triathlon.png";
import PhoneInTalkIcon from "@material-ui/icons/PhoneInTalk";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import DirectionsRunIcon from "@material-ui/icons/DirectionsRun";
import DirectionsBikeIcon from "@material-ui/icons/DirectionsBike";
import PoolIcon from "@material-ui/icons/Pool";
import LanguageIcon from "@material-ui/icons/Language";
import Relay from "@material-ui/icons/People";
import Facebook from "@material-ui/icons/Facebook";
// styles dédiés à ce composant (voir EpreuveDetail.scss)
import "./EpreuveDetail.scss";

// function to check if var are empty
function ifExists(item, titreRubrique, unity) {
  return item && titreRubrique !== "" ? (
    <span className="detail-line">
      <span className="detail-label">{titreRubrique}</span> : {item} {unity}
    </span>
  ) : null;
}

// ---- Helpers SEO ----

// Retire les balises HTML d'un texte (utile pour la meta description,
// qui ne doit contenir que du texte brut) et tronque à une longueur donnée.
function stripHtmlAndTruncate(html, maxLength) {
  if (!html) return "";
  const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1).trimEnd() + "…";
}

// Construit le JSON-LD (schema.org/SportsEvent) pour les résultats enrichis Google
function buildEventJsonLd(epreuve, pageUrl) {
  const startDate = epreuve.date ? epreuve.date.slice(0, 10) : undefined;
  const firstOrg =
    epreuve.organisation && epreuve.organisation[0]
      ? epreuve.organisation[0]
      : null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: epreuve.name_event,
    startDate,
    sport: "Triathlon",
    description: stripHtmlAndTruncate(epreuve.description, 500),
    url: pageUrl,
    location: {
      "@type": "Place",
      name: epreuve.city,
      address: {
        "@type": "PostalAddress",
        addressLocality: epreuve.city,
        addressRegion: epreuve.region,
        addressCountry: "FR",
      },
    },
  };

  if (firstOrg) {
    jsonLd.organizer = {
      "@type": "Organization",
      name: firstOrg.name_org,
      ...(firstOrg.website ? { url: firstOrg.website } : {}),
      ...(firstOrg.email ? { email: firstOrg.email } : {}),
    };
  }

  const pricedRace = epreuve.races.find((r) => r.price_triathlon);
  if (pricedRace) {
    jsonLd.offers = {
      "@type": "Offer",
      price: pricedRace.price_triathlon,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: pageUrl,
    };
  }

  return jsonLd;
}

export class EpreuveDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = { epreuve: null };
  }

  async componentDidMount() {
    const { epreuveId } = this.props.match.params;
    const epreuve = await loadEpreuve(epreuveId);
    this.setState({ epreuve });
  }

  // triathlon size
  regularDistance(param) {
    switch (param) {
      case "XS":
        return "400 m natation, 10 km vélo, 2,5 km course à pied.";
      case "S":
        return "750 m natation, 20 km vélo, 5 km course à pied.";
      case "M":
        return "1500 m natation, 40 km vélo, 10 km course à pied.";
      case "L":
        return "1900 m natation, 90 km vélo, 21 km course à pied.";
      case "XL":
        return "4000 m natation, 120 km vélo, 30 km course à pied.";
      case "XXL":
        return "3800 m natation, 180 km vélo, 42,195 km course à pied.";
      default:
        return null;
    }
  }
  // cross triathlon size
  regularCrossDistance(param) {
    switch (param) {
      case "XS":
        return "250 m natation, 5,5 km VTT, 2,2 km trail.";
      case "S":
        return "500 m natation, 11 km VTT, 4 km trail.";
      case "M":
        return "1000 m natation, 22 km VTT, 10 km trail.";
      case "L":
        return "2000 m natation, 44 km VTT, 16 km trail.";
      case "XL":
        return "3000 m natation, 66 km VTT, 24 km trail.";
      default:
        return null;
    }
  }

  render() {
    const { epreuve } = this.state;

    function createMarkup() {
      return { __html: epreuve.region };
    }

    if (!epreuve) {
      return "";
    }

    // Garde défensive : un tableau vide donne undefined à l'index 0,
    // pas null — "!== null" laissait donc passer ce cas et provoquait
    // un crash sur epreuve.organisation[0].name_org.
    const organisateur =
      epreuve.organisation && epreuve.organisation.length > 0
        ? epreuve.organisation[0]
        : null;

    // ---- SEO : titre, description, URL canonique, JSON-LD ----
    const formattedDate = epreuve.date
      ? moment(epreuve.date.slice(0, 10)).format("DD/MM/YYYY")
      : "";
    const pageTitle = `${epreuve.name_event} – ${epreuve.city} (${formattedDate}) | Calendrier Triathlon`;
    const pageDescription =
      stripHtmlAndTruncate(epreuve.description, 155) ||
      `Triathlon ${epreuve.name_event} à ${epreuve.city} le ${formattedDate}. Retrouvez les formats, l'organisateur et les infos pratiques.`;
    const canonicalUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}${window.location.pathname}`
        : undefined;
    const eventJsonLd = buildEventJsonLd(epreuve, canonicalUrl);

    // Corrigé : department est un nombre (pas de .length sur un Int),
    // on le convertit en chaîne avant de tester sa longueur pour le
    // zéro-préfixage ("1" -> "01").
    const departmentDisplay =
      epreuve.department != null && String(epreuve.department).length === 1
        ? "0" + epreuve.department
        : epreuve.department;

    return (
      <section className="epreuve-detail">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

          {/* Open Graph / partage réseaux sociaux */}
          <meta property="og:type" content="website" />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          {canonicalUrl ? (
            <meta property="og:url" content={canonicalUrl} />
          ) : null}

          {/* Données structurées schema.org pour les résultats enrichis */}
          <script type="application/ld+json">
            {JSON.stringify(eventJsonLd)}
          </script>
        </Helmet>

        {/* Image de couverture : full-bleed pour égaler la largeur de la
            navbar fixed-top, malgré le .container parent (voir App.jsx)
            qui limiterait sinon sa largeur. cover-banner fixe une hauteur
            cible et recadre proprement l'image dedans (object-fit: cover). */}
        <div className="full-bleed">
          <img
            src={ImageTriathlonDetail}
            alt="Description du triathlon sélectionné"
            className="cover-banner"
          />
        </div>

        {/* Bloc principal : colonne gauche (date/ville, carte, organisateur)
            + colonne droite (titre, description, formats de course) */}
        <div className="container mt-4">
          <div className="row">
            {/* ───── Colonne gauche : date/ville, puis map juste en dessous,
                puis organisateur (regroupés car tous liés à la localisation) ───── */}
            <div className="col-lg-5 col-md-6 mb-3">
              <div className="date-city-card mb-3">
                <span className="date-pill">{epreuve.date.slice(0, 10)}</span>
                <span className="city-pill">{epreuve.city}</span>
              </div>

              {epreuve.map_coord ? (
                <div className="side-card side-card--map">
                  <div className="side-card-title">Localisation</div>
                  <TriathlonLocation
                    coord={coord(epreuve.map_coord)}
                    popup={[
                      epreuve.id,
                      epreuve.name_event,
                      epreuve.races.map((r) => r.type_triathlon).join(", "),
                    ]}
                    region={epreuve.region}
                  />
                  <p className="detail-line mt-2">
                    Coordonnées : {epreuve.map_coord.split(":")[0]}
                  </p>
                </div>
              ) : null}

              {organisateur ? (
                <div className="side-card side-card--org">
                  <div className="side-card-title">Organisateur</div>
                  <h4 className="org-name">{organisateur.name_org}</h4>

                  <div className="mb-2">
                    {ifExists(organisateur.contact, "Contact")}
                    {ifExists(organisateur.address, "Adresse")}
                    <span className="detail-line">
                      {organisateur.zipcode} - {organisateur.city}
                    </span>
                  </div>

                  {organisateur.website ? (
                    <div className="org-contact-line">
                      <LanguageIcon fontSize="small" />
                      <a
                        href={organisateur.website}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {organisateur.website}
                      </a>
                    </div>
                  ) : null}

                  {organisateur.email ? (
                    <div className="org-contact-line">
                      <MailOutlineIcon fontSize="small" />
                      <a href={`mailto:${organisateur.email}`}>
                        {organisateur.email}
                      </a>
                    </div>
                  ) : null}

                  {organisateur.phonenumber ? (
                    <div className="org-contact-line">
                      <PhoneInTalkIcon fontSize="small" />
                      <span>{organisateur.phonenumber}</span>
                    </div>
                  ) : null}

                  {organisateur.facebook ? (
                    <div className="org-contact-line">
                      <a
                        href={organisateur.facebook}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Facebook fontSize="small" /> Page Facebook
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* ───── Colonne droite : titre + département, texte, cartes de courses ───── */}
            <div className="col-lg-7 col-md-6 mb-3">
              <div className="d-flex justify-content-between align-items-start flex-wrap">
                <div>
                  <div className="discipline-eyebrow">
                    <PoolIcon fontSize="small" />
                    <DirectionsBikeIcon fontSize="small" />
                    <DirectionsRunIcon fontSize="small" />
                    <span>Triathlon</span>
                  </div>

                  <h1 className="event-title">{epreuve.name_event}</h1>
                </div>

                <div className="department-badge">
                  <span className="department-label">Département</span>
                  <span className="department-value">{departmentDisplay}</span>
                </div>
              </div>

              <p
                className="text-muted region-line"
                dangerouslySetInnerHTML={createMarkup()}
              />

              <p className="format-line">
                <strong>
                  {epreuve.races.length > 1
                    ? `${epreuve.races.length} formats de course sont proposés`
                    : "Format de la course"}
                </strong>
                {" : "}
                {epreuve.races
                  .map((item) => item.type_triathlon)
                  .join(", ")}
              </p>

              <div
                className="event-description mb-4"
                dangerouslySetInnerHTML={{ __html: epreuve.description }}
              />

              <div className="race-cards-grid">
                {epreuve.races.map((item, key) => (
                  <div className="race-card" key={item.id || key}>
                    <span className="race-card-title">
                      Distance {item.type_triathlon}
                      {item.date && item.date !== epreuve.date
                        ? ` (${moment(item.date.slice(0, 10)).format(
                            "DD/MM/YYYY"
                          )})`
                        : null}
                    </span>

                    <p className="race-card-body">
                      {item.crosstri === "0" ? (
                        item.details || this.regularDistance(item.type_triathlon)
                      ) : (
                        <>
                          <span className="cross-tag">Cross</span>
                          {item.details ||
                            this.regularCrossDistance(item.type_triathlon)}
                        </>
                      )}
                    </p>

                    <div className="race-card-meta">
                      {item.Swimenv ? (
                        <span>
                          <PoolIcon fontSize="small" /> en {item.Swimenv}
                        </span>
                      ) : null}
                      {item.Elevation_bike ? (
                        <span>
                          <DirectionsBikeIcon fontSize="small" /> D+{" "}
                          {item.Elevation_bike} m
                        </span>
                      ) : null}
                      {item.Elevation_run ? (
                        <span>
                          <DirectionsRunIcon fontSize="small" /> D+{" "}
                          {item.Elevation_run} m
                        </span>
                      ) : null}
                    </div>

                    {item.price_triathlon || item.bibs || item.relay === "1" ? (
                      <div className="race-card-footer">
                        {ifExists(
                          item.price_triathlon,
                          "Inscription",
                          "euros"
                        )}
                        {item.price_triathlon && item.bibs ? " · " : null}
                        {ifExists(item.bibs, "Places disponibles")}
                        {item.relay === "1" ? (
                          <>
                            {(item.price_triathlon || item.bibs) && " · "}
                            <span className="relay-tag">
                              <Relay fontSize="small" /> Ouvert en relais
                            </span>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              {/* Dans la même région : rattachée à la colonne droite (sous les
                  cartes de courses), pas en pleine largeur sous les 2 colonnes */}
              <h3 className="section-heading mt-4">Dans la même région</h3>
              <QueryTriathlon
                regionFilter={epreuve ? epreuve.region : "Toute la France"}
                yearFilter={Number(epreuve.date.slice(-13, -9))}
              />
            </div>
          </div>
        </div>
      </section>
    );
  }
}
